import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CloseOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Flex, Modal, Typography } from 'antd';
import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import * as Sentry from '@sentry/react';
import { brokerCasesApi } from 'api/brokerCases';
import { CASE_TASKS_QUERY_KEY, DOCUMENT_REVIEWS_QUERY_KEY } from 'queries/brokerCases';
import { DocType } from 'enums';
import { EvidenceTaskName, EvidenceTaskNameMap } from 'api/brokerCases/types';
import { getDescriptionOptionsByTaskName, getDocTypeForTask } from 'pages/EvidencePage/evidenceConfig';
import { TypedFormatMessageFn } from 'types/intl';
import { EvidenceUploadDropzone } from './EvidenceUploadDropzone';
import { EvidenceUploadFileList } from './EvidenceUploadFileList';

const ACCEPT_FILE_TYPES = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png';
const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export interface IEvidenceUploadDialogProps {
  open: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
  onProgressLoadingChange?: (isLoading: boolean) => void;
  taskName: EvidenceTaskName;
  caseId: string;
  individualId?: string;
  taskId: string;
  taskDocumentType?: DocType;
  uploadDescriptionRequired?: boolean;
}

export const EvidenceUploadDialog = ({
  open,
  onCancel,
  onSuccess,
  onProgressLoadingChange,
  taskName,
  caseId,
  individualId,
  taskId,
  taskDocumentType,
  uploadDescriptionRequired = false,
}: IEvidenceUploadDialogProps) => {
  const { formatMessage }: { formatMessage: TypedFormatMessageFn } = useIntl();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [descriptions, setDescriptions] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const options = getDescriptionOptionsByTaskName(taskName, formatMessage);
  const isDescriptionEnabled =
    taskName === EvidenceTaskNameMap.OTHER_DOCUMENTS ||
    taskName === EvidenceTaskNameMap.FURTHER_INFORMATION_REQUIRED ||
    Boolean(options?.length);
  const isDescriptionRequired = uploadDescriptionRequired && isDescriptionEnabled;
  const hasAllRequiredDescriptions =
    !isDescriptionRequired ||
    (selectedFiles.length > 0 && selectedFiles.every((_, index) => (descriptions[index]?.trim() ?? '').length > 0));

  const processFiles = (files: File[]) => {
    if (!files.length) return;

    const valid = files.filter(file => file.size <= MAX_FILE_SIZE_BYTES);
    const skippedCount = files.length - valid.length;

    if (skippedCount > 0) {
      enqueueSnackbar(
        formatMessage({ id: 'evidence.uploadDialog.skippedFiles' }, { skippedCount, maxFileSizeMB: MAX_FILE_SIZE_MB }),
        {
          variant: 'warning',
          anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        },
      );
    }

    if (valid.length) {
      setSelectedFiles(prev => [...prev, ...valid]);
      setDescriptions(prev => [...prev, ...valid.map(() => '')]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files?.length) {
      processFiles(Array.from(files));
    }
    e.target.value = '';
  };

  const handleConfirm = async () => {
    if (isDescriptionRequired) {
      const hasMissing = selectedFiles.some((_, index) => !(descriptions[index]?.trim() ?? ''));
      if (hasMissing) {
        enqueueSnackbar(formatMessage({ id: 'evidence.uploadDialog.missingDescriptionWarning' }), {
          variant: 'warning',
          anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        });
        return;
      }
    }

    try {
      setIsUploading(true);
      onProgressLoadingChange?.(true);
      const type = taskDocumentType ?? getDocTypeForTask(taskName);

      if (!type) {
        Sentry.captureException(new Error('Evidence upload missing task.payload.type'), {
          tags: { area: 'evidence', operation: 'upload-doc' },
          extra: { caseId, taskId, taskName, individualId },
        });

        enqueueSnackbar(formatMessage({ id: 'evidence.uploadDialog.missingDocumentTypeError' }), {
          variant: 'error',
          anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        });

        setIsUploading(false);
        onProgressLoadingChange?.(false);

        return;
      }

      const meta = selectedFiles.map((_, index) => ({
        individualId,
        type,
        description: isDescriptionEnabled ? descriptions[index] : undefined,
      }));
      await brokerCasesApi.uploadDoc(caseId, selectedFiles, meta, taskId);

      await queryClient.invalidateQueries({ queryKey: [CASE_TASKS_QUERY_KEY, caseId] });
      await queryClient.invalidateQueries({ queryKey: [DOCUMENT_REVIEWS_QUERY_KEY, caseId] });

      onProgressLoadingChange?.(false);
      setIsUploading(false);

      setSelectedFiles([]);
      setDescriptions([]);
      onSuccess?.();
    } catch (error) {
      onProgressLoadingChange?.(false);
      setIsUploading(false);
      enqueueSnackbar(
        error instanceof Error ? error.message : formatMessage({ id: 'evidence.uploadDialog.uploadFailed' }),
        {
          variant: 'error',
          anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        },
      );
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setDescriptions(prev => prev.filter((_, i) => i !== index));
  };

  const handleSetDescription = (index: number, value: string) => {
    setDescriptions(prev => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={null}
      footer={null}
      closable={false}
      maskClosable={false}
      classNames={{
        content: 'rounded-2xl p-0',
        body: 'px-6 pt-4 pb-6',
      }}
      width={412}
    >
      <Flex justify='space-between' align='center' className='mb-2'>
        <Flex vertical className='gap-2'>
          <Flex align='center' justify='space-between' className='gap-2'>
            <Typography.Text className='font-bold text-base leading-6 text-black/90'>
              {formatMessage({ id: 'evidence.uploadDialog.title' })}
            </Typography.Text>
            <Button
              type='text'
              disabled={isUploading}
              className='flex h-6 min-h-6 w-6 min-w-6 items-center justify-center border-0 bg-transparent p-0.5 shadow-none box-border'
              icon={<CloseOutlined className='text-xs leading-none' />}
              onClick={onCancel}
            />
          </Flex>
          <Flex align='flex-start items-center' gap={8}>
            <ExclamationCircleOutlined className='shrink-0 text-base leading-6 text-orange-500' />
            <p className='text-sm leading-5 text-orange-500 py-0 my-0'>
              <span
                dangerouslySetInnerHTML={{ __html: formatMessage({ id: 'evidence.uploadDialog.beforeConfirmHint' }) }}
              />
            </p>
          </Flex>
        </Flex>
      </Flex>
      <EvidenceUploadDropzone
        inputRef={fileInputRef}
        onFileChange={handleFileChange}
        onFilesSelected={processFiles}
        accept={ACCEPT_FILE_TYPES}
        maxFileSizeMB={MAX_FILE_SIZE_MB}
      />
      <EvidenceUploadFileList
        files={selectedFiles}
        onRemove={handleRemoveFile}
        setDescription={handleSetDescription}
        options={isDescriptionEnabled ? options : undefined}
        allowDescription={isDescriptionEnabled}
        descriptionRequired={isDescriptionRequired}
        descriptions={descriptions}
      />
      <Flex justify='flex-end' className='mt-6' gap={8}>
        <Button
          type='default'
          className='flex items-center border border-slate-300 bg-transparent px-4 py-1.5 shadow-none'
          disabled={isUploading}
          onClick={onCancel}
        >
          <span className='text-xs font-normal leading-5'>{formatMessage({ id: 'evidence.uploadDialog.cancel' })}</span>
        </Button>
        <Button
          type='default'
          disabled={!selectedFiles.length || !hasAllRequiredDescriptions}
          loading={isUploading}
          className='border border-none bg-green-500 px-4 py-1.5 font-normal text-xs leading-5 text-white shadow-none enabled:hover:!bg-green-600 enabled:active:!bg-green-700'
          onClick={handleConfirm}
        >
          {formatMessage({ id: 'evidence.uploadDialog.confirmUpload' })}
        </Button>
      </Flex>
    </Modal>
  );
};
